import { useCallback } from 'react';

import { link, routes } from '@aline/routes';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

/**
 * Returns the route that shows the default AI chat drawer for the current page.
 * For editor routes: navigates to /doc/:documentId/ai
 * For non-editor routes: navigates to the parent route (which shows AI chat as default)
 */
export function getBackToAIRoute(pathname: string): string | null {
  const docMatch = matchPath({ end: false, path: routes.DocumentDetail }, pathname);
  if (docMatch?.params.documentId)
    return link.DocumentDetail({ documentId: docMatch.params.documentId });

  const repoMatch = matchPath(routes.RepositoryDocumentPreview, pathname);
  if (repoMatch) return link.RepositoryDocuments();

  const partyMatch = matchPath(routes.PartyDetailDocumentPreview, pathname);
  if (partyMatch?.params.partyId)
    return link.PartyDetailDocuments({ partyId: partyMatch.params.partyId });

  const reminderMatch = matchPath(routes.RemindersPreview, pathname);
  if (reminderMatch) return link.RemindersIndex();

  const reportAutomationsMatch = matchPath(routes.ReportReportDetailAutomations, pathname);
  if (reportAutomationsMatch?.params.reportId) {
    return link.ReportDetail({ reportId: reportAutomationsMatch.params.reportId });
  }

  return null;
}

/**
 * Hook that returns a callback to navigate back to AI chat.
 * Uses the current pathname to determine the correct back route.
 */
export function useBackToAI(): () => void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { openDrawer } = useGlobalDrawer();

  return useCallback(() => {
    const backRoute = getBackToAIRoute(pathname);
    if (backRoute) {
      navigate(backRoute, { replace: true });
      openDrawer();
    }
  }, [navigate, openDrawer, pathname]);
}
